const React = require('react')
const { useCallback, useEffect, useState } = require('react')
const { Col, Row } = require('reactstrap')
const moment = require('moment')
const { useNetworkConfig } = require('@contexts')
const { useBlockchainData } = require('@hooks')
const { CustomDropdown, CustomLineChart } = require('@components/common')

require('@styles/pages/home/generalInfo.home/chart.general.home.css')

const GeneralChart = () => {
    const { network } = useNetworkConfig()
    const { getTransactionPerDay } = useBlockchainData(network.name)
    const [dataChart, setDataChart] = useState([0, 0, 0, 0, 0, 0])

    const series = [
        {
            name: 'Transactions',
            data: dataChart
        }
    ]

    const [range, setRange] = useState(() => {
        const result = []
        // For 7 days
        for (let index = 6; index >= 0; index--) {
            const currentTime = moment().subtract(index, 'd')
            result.push([
                currentTime.date(),
                currentTime.month(),
                currentTime.year()
            ])
        }
        return {
            label: result.map((item, index) =>
                index === result.length - 1
                    ? 'Today'
                    : `${item[0]}/${item[1] + 1}`
            ),
            data: result,
            title: '7 Days'
        }
    })

    const formatHourToAMPM = hour => {
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const h = hour % 12 || 12
        return `${h}${ampm}`
    }

    const changeFilter = useCallback(async value => {
        const ranges = {
            '1 day': {
                data: Array.from({ length: 6 }, (_, i) => {
                    const time = moment()
                        .startOf('day')
                        .add(i * 4, 'hours')
                    return [time.hour()]
                }),
                formatLabel: item => {
                    const start = formatHourToAMPM(item[0])
                    const end = formatHourToAMPM(item[0] + 4)
                    return `${start}-${end}`
                },
                title: '24 Hours'
            },
            '7 days': {
                data: Array.from({ length: 7 }, (_, i) => {
                    const time = moment().subtract(6 - i, 'days')
                    return [time.date(), time.month(), time.year()]
                }),
                formatLabel: (item, index, arr) =>
                    index === arr.length - 1
                        ? 'Today'
                        : `${item[0]}/${item[1] + 1}`,
                title: '7 Days'
            },
            '30 days': {
                data: Array.from({ length: 6 }, (_, i) => {
                    const endDate = moment().subtract(i * 5, 'days')
                    const startDate = moment().subtract(i * 5 + 4, 'days')
                    return [startDate, endDate]
                }).reverse(),
                formatLabel: item => {
                    const startDate = moment(item[0])
                    const endDate = moment(item[1])
                    return `${startDate.format('D/M')}-${endDate.format('D/M')}`
                },
                title: '30 Days'
            },
            '90 days': {
                data: Array.from({ length: 6 }, (_, i) => {
                    const endDate = moment().subtract(i * 15, 'days')
                    const startDate = moment().subtract(i * 15 + 14, 'days')
                    return [startDate, endDate]
                }).reverse(),
                formatLabel: item => {
                    const startDate = moment(item[0])
                    const endDate = moment(item[1])
                    return `${startDate.format('D/M')}-${endDate.format('D/M')}`
                },
                title: '90 Days'
            }
        }

        const selectedRange = ranges[value]
        if (selectedRange) {
            setRange({
                data: selectedRange.data,
                label: selectedRange.data.map(selectedRange.formatLabel),
                title: selectedRange.title
            })
        }
    }, [])

    const fetchData = useCallback(async () => {
        const days =
            range.title === '24 Hours'
                ? 1
                : range.title === '7 Days'
                ? 7
                : range.title === '30 Days'
                ? 30
                : 90
        const result = await getTransactionPerDay(days)
        if (result && result.length > 0) {
            if (range.title === '24 Hours') {
                const splitData = Array(6).fill(0)
                result.forEach((value, index) => {
                    const slot = Math.floor((index * 6) / 24)
                    splitData[slot] = (splitData[slot] || 0) + value
                })
                setDataChart(splitData)
            } else if (range.title === '30 Days') {
                const splitData = Array(6).fill(0)
                result.forEach((value, index) => {
                    const slot = Math.floor(index / 5)
                    splitData[slot] = (splitData[slot] || 0) + value
                })
                setDataChart(splitData)
            } else if (range.title === '90 Days') {
                const splitData = Array(6).fill(0)
                result.forEach((value, index) => {
                    const slot = Math.floor(index / 15)
                    splitData[slot] = (splitData[slot] || 0) + value
                })
                setDataChart(splitData)
            } else {
                setDataChart(result)
            }
        }
    }, [getTransactionPerDay, range.title])

    useEffect(() => {
        fetchData()
    }, [network.name, fetchData])

    useEffect(() => {
        fetchData()
    }, [range.title])

    return (
        <>
            <Row>
                <Col className='chart-header'>
                    <span className='chart-title'>Transaction history in</span>
                    <CustomDropdown
                        currentItem={{ title: range.title }}
                        listItems={[
                            { value: '1 day', name: '24 Hours' },
                            { value: '7 days', name: '7 Days' },
                            { value: '30 days', name: '30 Days' },
                            { value: '90 days', name: '90 Days' }
                        ]}
                        handleClick={changeFilter}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <CustomLineChart
                        colors={network.data.chartStyle}
                        labels={range.label}
                        series={series}
                    />
                </Col>
            </Row>
        </>
    )
}

module.exports = { GeneralChart }
